<!-- ビデオプレイヤー -->
<template>
  <div class="video-container" @click="togglePlay" @contextmenu.prevent>
    <video
      ref="videoRef"
      controls
      controlsList="nodownload"
      @play="logPlayEvent"
      @pause="logPauseEvent"
      @ended="logEndedEvent"
      @timeupdate="checkHalfWatched"
      @click.stop
    >
      <source :src="videoSrc" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
    <div v-if="!isPlaying" class="play-button" @click.stop="togglePlay"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from "vue";
import { outputDebugLogs } from "@/Common";
import logService from "@/services/logService";
import { AppConstants } from "@/constants/AppConstants";

export default defineComponent({
  name: "VideoPlayer",
  props: {
    videoSrc: String,
    contentsId: String,
    screenName: String,
  },
  emits: ["half-watched"],
  setup(props, { emit }) {
    const videoRef = ref<HTMLVideoElement | null>(null);
    const startTime: Ref<number | null> = ref(null); // 再生開始時間を格納する変数
    const isPlaying = ref(false); // 再生状態を管理する変数
    const isHalfWatched = ref(false);
    const agCode = ref(""); // AGコード
    const userId = ref(""); // ユーザーID

    // 再生開始時の処理
    const logPlayEvent = () => {
      if (videoRef.value) {
        startTime.value = videoRef.value.currentTime;
        isPlaying.value = true; // 再生状態を更新
        if (props.videoSrc != undefined) {
          outputDebugLogs(props.videoSrc);
        }
        outputDebugLogs(
          `Playback started at: ${startTime.value.toFixed(2)} seconds`
        );

        // 動画再生開始のログを出力
        regLogData(
          "動画再生開始",
          `再生開始時間：${startTime.value.toFixed(2)}`
        );
      }
    };

    // 停止時の処理
    const logPauseEvent = () => {
      if (videoRef.value && startTime.value !== null) {
        const endTime = videoRef.value.currentTime;
        const elapsedTime = endTime - startTime.value;
        isPlaying.value = false; // 再生状態を更新
        if (props.videoSrc != undefined) {
          outputDebugLogs(props.videoSrc);
        }
        outputDebugLogs(`Playback paused at: ${endTime.toFixed(2)} seconds`);
        outputDebugLogs(
          `Total playback time this session: ${elapsedTime.toFixed(2)} seconds`
        );

        // 動画再生停止のログを出力
        regLogData(
          "動画再生停止",
          `再生停止時間：${endTime.toFixed(2)} 視聴時間：${elapsedTime.toFixed(
            2
          )}`
        );
        startTime.value = null; // 次回の再生用に開始時間をリセット
      }
    };

    /** 動画視聴完了 */
    const logEndedEvent = () => {
      if (videoRef.value) {
        outputDebugLogs(
          `Video ended at: ${videoRef.value.currentTime.toFixed(2)} seconds`
        );
        // ここで、動画が最後まで再生されたことを検知します

        // 動画再生停止のログを出力
        regLogData("動画視聴完了", "");
      }
    };

    /** 半分まで見た */
    const checkHalfWatched = () => {
      if (videoRef.value && !isHalfWatched.value) {
        const currentTime = videoRef.value.currentTime;
        const duration = videoRef.value.duration;
        if (currentTime >= duration / 2) {
          isHalfWatched.value = true;
          emit("half-watched", true);
          outputDebugLogs("Video has been watched halfway.");
        }
      }
    };

    const togglePlay = () => {
      if (videoRef.value) {
        if (isPlaying.value) {
          videoRef.value.pause();
        } else {
          videoRef.value.play();
        }
      }
    };

    const regLogData = (actionDetail: string, genericField3: string) => {
      // ローカルストレージからユーザー情報を取得して内部変数に展開
      const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
      if (userInfo) {
        const parsedUserInfo = JSON.parse(userInfo);
        agCode.value = parsedUserInfo.agCode;
        userId.value = parsedUserInfo.userId;
      }

      // ログを出力
      const logData = {
        systemkbn: process.env.VUE_APP_SYSTEM_KBN,
        userId: userId.value,
        agCode: agCode.value,
        actionTime: new Date().toISOString(),
        operationType: AppConstants.OP_TYPE_VIDEO,
        pseudoAgCode: "",
        pseudoUserId: "",
        fromScreenName: props.screenName || "",
        toScreenName: "",
        controlName: "",
        actionDetail: actionDetail,
        genericField1: "コンテンツID：" + props.contentsId,
        genericField2: "動画ファイル名：" + props.videoSrc,
        genericField3: genericField3,
        currentUrl: window.location.href,
      };

      logService.sendLog(logData);
    };

    return {
      videoRef,
      logPlayEvent,
      logPauseEvent,
      logEndedEvent,
      checkHalfWatched,
      togglePlay,
      isPlaying,
    };
  },
});
</script>

<style scoped>
.video-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
}

video {
  width: 100%;
  height: 100%;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: url("@/assets/common/img/icon-play1.png") no-repeat center center;
  background-size: contain;
  cursor: pointer;
}
</style>
